(() => {
  const LEADS_ENDPOINT = 'https://redlemon-clientify-leads.shimele.workers.dev/v1/leads';
  const LEAD_TRACKING_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
  const LEAD_TRACKING_STORAGE_KEY = 'rl_lead_tracking';

  const getShopRoot = () => window.Shopify?.routes?.root || '/';

  const getCleanUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (_) {
      return '';
    }
  };

  const getLandingHandle = (pathname = window.location.pathname) => {
    const parts = pathname.split('/').filter(Boolean);
    return parts.at(-1) || 'home';
  };

  const getLeadChannel = (tracking) => {
    const source = String(tracking.utm_source || '').trim().toLowerCase();
    const medium = String(tracking.utm_medium || '').trim().toLowerCase();
    const paidMedium = /(cpc|ppc|paid|display|retargeting|remarketing)/.test(medium);

    if (tracking.gclid || (source.includes('google') && paidMedium)) return 'google_ads';
    if (tracking.fbclid || (/(facebook|instagram|meta)/.test(source) && paidMedium)) return 'meta_ads';
    if (source && medium.includes('organic')) return `${source}_organic`;
    if (source) return source.slice(0, 80);

    const referrerValue = tracking.referrer_url;
    if (!referrerValue) return 'direct';

    try {
      const referrer = new URL(referrerValue);
      if (referrer.origin === window.location.origin) return 'internal';
      if (/(^|\.)google\./.test(referrer.hostname)) return 'google_organic';
      if (/(^|\.)bing\.com$/.test(referrer.hostname)) return 'bing_organic';
      if (/(^|\.)duckduckgo\.com$/.test(referrer.hostname)) return 'duckduckgo_organic';
      if (/(facebook|instagram|tiktok|linkedin)\.com$/.test(referrer.hostname)) return 'organic_social';
      return 'referral';
    } catch (_) {
      return 'unknown';
    }
  };

  const getLeadTracking = () => {
    let tracking = {};
    try {
      tracking = JSON.parse(window.sessionStorage.getItem(LEAD_TRACKING_STORAGE_KEY) || '{}');
    } catch (_) {
      tracking = {};
    }

    const params = new URLSearchParams(window.location.search);
    LEAD_TRACKING_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) tracking[key] = value.slice(0, 250);
    });

    if (!Object.hasOwn(tracking, 'landing_url')) {
      tracking.landing_url = getCleanUrl(window.location.href);
      tracking.landing_handle = getLandingHandle();
      tracking.referrer_url = getCleanUrl(document.referrer);
    }
    tracking.acquisition_channel = getLeadChannel(tracking);

    try {
      window.sessionStorage.setItem(LEAD_TRACKING_STORAGE_KEY, JSON.stringify(tracking));
    } catch (_) {
      // The form still works when storage is disabled.
    }

    return tracking;
  };

  const collectLeadPayload = (container, form) => {
    const tracking = getLeadTracking();
    const payload = {
      form_type: container.dataset.formType || 'mayoreo',
      page_url: getCleanUrl(window.location.href),
      ...tracking
    };

    form.querySelectorAll('[data-rl-lead-field]').forEach((field) => {
      const key = field.dataset.rlLeadField;
      if (!key || field.disabled) return;

      let value = field.value;
      if (field.type === 'checkbox') {
        value = key === 'consent' ? field.checked : (field.checked ? field.value : '');
      }

      if (value !== '') payload[key] = value;
    });

    const isSupportRequest = payload.form_type === 'garantia' || payload.source === 'web_warranty';
    payload.lead_category = isSupportRequest ? 'support' : 'commercial';
    payload.analytics_event = isSupportRequest ? 'support_request_submit' : 'b2b_lead_submit';
    payload.landing_handle ||= getLandingHandle();
    payload.interest ||= payload.product_title || payload.request_type || payload.form_type;
    payload.source ||= 'web';
    payload.message = [
      payload.message,
      '',
      'Atribución web:',
      `Categoría: ${payload.lead_category}`,
      `Canal: ${payload.acquisition_channel}`,
      `Landing: ${payload.landing_handle}`,
      `Interés: ${payload.interest}`
    ].filter(Boolean).join('\n');

    return payload;
  };

  const publishLeadConversion = (payload, requestId) => {
    const eventName = payload.analytics_event || 'b2b_lead_submit';
    const eventData = {
      form_type: payload.form_type || 'mayoreo',
      lead_category: payload.lead_category || 'commercial',
      landing_handle: payload.landing_handle || '',
      interest: payload.interest || '',
      acquisition_channel: payload.acquisition_channel || 'unknown',
      source: payload.source || 'web',
      event_id: requestId,
      request_id: requestId
    };

    document.dispatchEvent(new CustomEvent('rl:lead-sent', {
      detail: { eventName, ...eventData }
    }));

    const shopifyEvent = window.Shopify?.analytics?.publish?.(`redlemon:${eventName}`, eventData);
    if (shopifyEvent?.catch) shopifyEvent.catch(() => {});

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventData);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...eventData });
    }
  };

  const initLeadForms = (root) => {
    const containers = root.matches?.('[data-rl-lead-form]')
      ? [root]
      : [...(root.querySelectorAll?.('[data-rl-lead-form]') || [])];

    containers.forEach((container) => {
      if (container.dataset.rlLeadInitialized === 'true') return;
      const form = container.matches('form') ? container : container.querySelector('form');
      if (!form) return;

      container.dataset.rlLeadInitialized = 'true';
      const fields = form.querySelector('[data-rl-lead-fields]');
      const success = form.querySelector('[data-rl-lead-success]');
      const error = form.querySelector('[data-rl-lead-error]');
      const submit = form.querySelector('[data-rl-lead-submit]');
      const startedAt = form.querySelector('[data-rl-lead-field="_started_at"]');
      if (startedAt) startedAt.value = Date.now();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!submit || container.dataset.rlSending === 'true') return;

        container.dataset.rlSending = 'true';
        container.setAttribute('aria-busy', 'true');
        success?.setAttribute('hidden', '');
        error?.setAttribute('hidden', '');

        const originalButton = submit.innerHTML;
        submit.disabled = true;
        submit.classList.add('is-loading');
        submit.textContent = submit.dataset.pendingLabel || 'Enviando…';

        try {
          const leadPayload = collectLeadPayload(container, form);
          const response = await fetch(LEADS_ENDPOINT, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(leadPayload)
          });

          let result = {};
          try {
            result = await response.json();
          } catch (_) {
            result = {};
          }

          if (!response.ok || result.ok !== true) {
            throw new Error(result.error || error?.dataset.defaultMessage || 'No pudimos enviar tu solicitud.');
          }

          form.reset();
          if (startedAt) startedAt.value = Date.now();
          fields?.setAttribute('hidden', '');
          if (success) {
            success.dataset.requestId = result.request_id || '';
            success.removeAttribute('hidden');
            success.focus({ preventScroll: true });
          }
          publishLeadConversion(leadPayload, result.request_id || '');
        } catch (requestError) {
          if (error) {
            error.textContent = requestError.message || error.dataset.defaultMessage;
            error.removeAttribute('hidden');
            error.focus({ preventScroll: true });
          }
        } finally {
          submit.disabled = false;
          submit.classList.remove('is-loading');
          submit.innerHTML = originalButton;
          container.dataset.rlSending = 'false';
          container.removeAttribute('aria-busy');
        }
      }, { capture: true });
    });
  };

  const getCart = async () => {
    const response = await fetch(`${getShopRoot()}cart.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Unable to fetch cart');
    return response.json();
  };

  const publishCart = (cart, open = false) => {
    document.querySelectorAll('.rl-cart-link > span').forEach((badge) => {
      badge.textContent = cart.item_count;
    });
    document.dispatchEvent(new CustomEvent('rl:cart-updated', { detail: { cart, open } }));
  };

  const addFormToCart = async (form) => {
    const addUrl = new URL(form.action, window.location.origin);
    addUrl.pathname = addUrl.pathname.replace(/\/cart\/add\/?$/, '/cart/add.js');
    const response = await fetch(addUrl, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Unable to add product');
    const cart = await getCart();
    publishCart(cart, true);
    return cart;
  };

  const initProduct = (root) => {
    if (!root || root.dataset.rlInitialized === 'true') return;
    root.dataset.rlInitialized = 'true';

    const gallery = root.querySelector('[data-rl-gallery]');
    const galleryStage = gallery?.querySelector('.rl-gallery__stage');
    const thumbs = gallery ? [...gallery.querySelectorAll('[data-rl-thumb]')] : [];
    const slides = gallery ? [...gallery.querySelectorAll('[data-rl-slide]')] : [];
    const counter = gallery?.querySelector('[data-rl-gallery-counter]');
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const showSlide = (nextIndex) => {
      if (!slides.length) return;
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => slide.classList.toggle('is-active', index === activeIndex));
      thumbs.forEach((thumb, index) => thumb.classList.toggle('is-active', index === activeIndex));
      thumbs[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      if (counter) counter.textContent = `${activeIndex + 1} / ${slides.length}`;
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => showSlide(index)));
    gallery?.querySelector('[data-rl-gallery-prev]')?.addEventListener('click', () => showSlide(activeIndex - 1));
    gallery?.querySelector('[data-rl-gallery-next]')?.addEventListener('click', () => showSlide(activeIndex + 1));
    showSlide(activeIndex);

    let touchStartX = 0;
    let touchStartY = 0;
    galleryStage?.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStartX = touch?.clientX || 0;
      touchStartY = touch?.clientY || 0;
    }, { passive: true });
    galleryStage?.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const distanceX = touch.clientX - touchStartX;
      const distanceY = touch.clientY - touchStartY;
      if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
      showSlide(distanceX < 0 ? activeIndex + 1 : activeIndex - 1);
    }, { passive: true });

    const form = root.querySelector('.rl-product-form');
    const variantInput = form?.querySelector('[data-rl-variant-input]');
    const variantButtons = [...root.querySelectorAll('[data-rl-variant]')];
    const price = root.querySelector('[data-rl-price]');
    const mobilePrice = root.querySelector('[data-rl-mobile-price]');
    const comparePrice = root.querySelector('[data-rl-compare-price]');
    const addButton = form?.querySelector('[data-rl-add]');
    const addLabel = form?.querySelector('[data-rl-add-label]');
    const mobileAdd = root.querySelector('[data-rl-mobile-add]');
    const skuWrap = root.querySelector('[data-rl-sku-wrap]');
    const sku = root.querySelector('[data-rl-sku]');
    const b2bVariantInput = root.querySelector('[data-rl-b2b-variant]');
    const b2bVariantLabel = root.querySelector('[data-rl-b2b-variant-label]');
    const b2bVariantIdInput = root.querySelector('[data-rl-b2b-variant-id]');
    const b2bSkuInput = root.querySelector('[data-rl-b2b-sku]');
    const floatingPrice = root.querySelector('[data-rl-floating-price]');
    const floatingCompare = root.querySelector('[data-rl-floating-compare]');
    const floatingImage = root.querySelector('.rl-description__purchase-media img');
    const floatingStock = root.querySelector('[data-rl-floating-stock]');
    const floatingStockLabel = root.querySelector('[data-rl-floating-stock-label]');
    const floatingAdd = root.querySelector('[data-rl-floating-add]');

    variantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        variantButtons.forEach((item) => item.classList.toggle('is-active', item === button));
        if (variantInput) variantInput.value = button.dataset.rlVariant;
        if (price) price.textContent = button.dataset.price;
        if (mobilePrice) mobilePrice.textContent = button.dataset.price;
        if (floatingPrice) floatingPrice.textContent = button.dataset.price;
        if (comparePrice) {
          comparePrice.textContent = button.dataset.comparePrice;
          comparePrice.hidden = button.dataset.hasCompare !== 'true';
        }
        if (floatingCompare) {
          floatingCompare.textContent = button.dataset.comparePrice;
          floatingCompare.hidden = button.dataset.hasCompare !== 'true';
        }

        const available = button.dataset.available === 'true';
        if (addButton) addButton.disabled = !available;
        if (mobileAdd) {
          mobileAdd.disabled = !available;
          mobileAdd.textContent = available ? mobileAdd.dataset.availableLabel : mobileAdd.dataset.soldOutLabel;
        }
        if (addLabel) addLabel.textContent = available ? addLabel.dataset.availableLabel : addLabel.dataset.soldOutLabel;
        const selectedSku = button.dataset.sku || '';
        if (sku) sku.textContent = selectedSku;
        if (skuWrap) skuWrap.hidden = !selectedSku;
        if (floatingAdd) {
          floatingAdd.disabled = !available;
          floatingAdd.querySelector('span').textContent = available ? floatingAdd.dataset.availableLabel : floatingAdd.dataset.soldOutLabel;
        }
        floatingStock?.classList.toggle('is-out', !available);
        if (floatingStockLabel) floatingStockLabel.textContent = available
          ? floatingStockLabel.dataset.availableLabel || 'En existencia'
          : floatingStockLabel.dataset.soldOutLabel || 'Agotado';

        if (floatingImage && button.dataset.featuredImage) {
          floatingImage.removeAttribute('srcset');
          floatingImage.removeAttribute('sizes');
          floatingImage.src = button.dataset.featuredImage;
        }

        const variantTitle = button.querySelector('span')?.textContent.trim() || '';
        if (b2bVariantInput) b2bVariantInput.value = variantTitle;
        if (b2bVariantLabel) b2bVariantLabel.textContent = variantTitle;
        if (b2bVariantIdInput) b2bVariantIdInput.value = button.dataset.rlVariant || '';
        if (b2bSkuInput) b2bSkuInput.value = selectedSku;

        const mediaId = button.dataset.featuredMediaId;
        if (mediaId) {
          const mediaIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
          if (mediaIndex >= 0) showSlide(mediaIndex);
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', button.dataset.rlVariant);
        window.history.replaceState({}, '', url);
      });
    });

    const quantity = form?.querySelector('[data-rl-quantity]');
    form?.querySelector('[data-rl-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
    });
    form?.querySelector('[data-rl-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
    });
    quantity?.addEventListener('change', () => {
      quantity.value = Math.max(1, Number(quantity.value || 1));
    });

    form?.addEventListener('submit', async (event) => {
      if (event.submitter && event.submitter !== addButton) return;
      event.preventDefault();
      if (!addButton || addButton.disabled || form.dataset.rlSubmitting === 'true') return;

      form.dataset.rlSubmitting = 'true';
      const initiallyAvailable = !addButton.disabled;
      const purchaseButtons = [addButton, mobileAdd, floatingAdd].filter(Boolean);
      purchaseButtons.forEach((button) => { button.disabled = true; });
      if (addLabel) addLabel.textContent = addLabel.dataset.pendingLabel || addLabel.textContent;

      try {
        await addFormToCart(form);
        if (addLabel) addLabel.textContent = addLabel.dataset.successLabel || addLabel.textContent;
      } catch (_) {
        document.dispatchEvent(new CustomEvent('rl:cart-error'));
        if (addLabel) addLabel.textContent = addLabel.dataset.availableLabel;
      } finally {
        window.setTimeout(() => {
          const selectedVariant = variantButtons.find((button) => button.classList.contains('is-active'));
          const available = selectedVariant ? selectedVariant.dataset.available === 'true' : initiallyAvailable;
          purchaseButtons.forEach((button) => { button.disabled = !available; });
          if (addLabel) addLabel.textContent = available ? addLabel.dataset.availableLabel : addLabel.dataset.soldOutLabel;
          form.dataset.rlSubmitting = 'false';
        }, 900);
      }
    });

    mobileAdd?.addEventListener('click', () => form?.requestSubmit(addButton));
    floatingAdd?.addEventListener('click', () => form?.requestSubmit(addButton));

    const mobileBar = root.querySelector('[data-rl-mobile-buybar]');
    const purchaseRow = form?.querySelector('.rl-purchase-row');
    let mobileBarFrame = 0;
    const updateMobileBar = () => {
      mobileBarFrame = 0;
      if (!mobileBar || !purchaseRow || window.innerWidth > 820) {
        mobileBar?.classList.remove('is-visible');
        return;
      }
      const sectionRect = root.getBoundingClientRect();
      const purchaseRect = purchaseRow.getBoundingClientRect();
      const purchaseIsVisible = purchaseRect.bottom > 0 && purchaseRect.top < window.innerHeight;
      const sectionIsRelevant = sectionRect.top < -120 && sectionRect.bottom > window.innerHeight;
      mobileBar.classList.toggle('is-visible', sectionIsRelevant && !purchaseIsVisible);
    };
    const requestMobileBarUpdate = () => {
      if (mobileBarFrame) return;
      mobileBarFrame = window.requestAnimationFrame(updateMobileBar);
    };
    window.addEventListener('scroll', requestMobileBarUpdate, { passive: true });
    window.addEventListener('resize', requestMobileBarUpdate, { passive: true });
    updateMobileBar();

    const b2bDrawer = root.querySelector('[data-rl-b2b-drawer]');
    const b2bOpeners = [...root.querySelectorAll('[data-rl-b2b-open]')];
    const b2bClosers = [...root.querySelectorAll('[data-rl-b2b-close]')];
    const b2bWhatsapp = root.querySelector('[data-rl-b2b-whatsapp]');
    let b2bLastTrigger = null;

    const updateB2bWhatsapp = () => {
      if (!b2bWhatsapp) return;
      const quantityValue = root.querySelector('[data-rl-b2b-quantity]')?.value || '';
      const personalized = root.querySelector('[data-rl-b2b-personalization]')?.checked;
      const variantValue = b2bVariantInput?.value || b2bVariantLabel?.textContent.trim() || '';
      const lines = [
        `Hola, quiero cotizar ${quantityValue || 'varias piezas'} de ${b2bWhatsapp.dataset.product || 'este producto'}.`,
        variantValue && variantValue !== 'Default Title' ? `Variante: ${variantValue}.` : '',
        personalized ? 'Me interesa personalización con logotipo.' : '',
        b2bWhatsapp.dataset.url || window.location.href,
      ].filter(Boolean);
      b2bWhatsapp.href = `https://wa.me/${b2bWhatsapp.dataset.phone}?text=${encodeURIComponent(lines.join('\n'))}`;
    };

    const openB2bDrawer = (trigger) => {
      if (!b2bDrawer) return;
      b2bLastTrigger = trigger || document.activeElement;
      updateB2bWhatsapp();
      b2bDrawer.classList.add('is-open');
      b2bDrawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('rl-b2b-drawer-open');
      window.setTimeout(() => b2bDrawer.querySelector('[data-rl-b2b-close]:not(.rl-b2b-drawer__backdrop)')?.focus(), 60);
    };

    const closeB2bDrawer = () => {
      if (!b2bDrawer) return;
      b2bDrawer.classList.remove('is-open');
      b2bDrawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('rl-b2b-drawer-open');
      b2bLastTrigger?.focus?.();
    };

    b2bOpeners.forEach((button) => button.addEventListener('click', () => openB2bDrawer(button)));
    b2bClosers.forEach((button) => button.addEventListener('click', closeB2bDrawer));
    root.querySelector('[data-rl-b2b-quantity]')?.addEventListener('change', updateB2bWhatsapp);
    root.querySelector('[data-rl-b2b-personalization]')?.addEventListener('change', updateB2bWhatsapp);
    b2bWhatsapp?.addEventListener('click', updateB2bWhatsapp);
    b2bDrawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeB2bDrawer();
    });
    if (root.querySelector('[data-rl-b2b-success]:not([hidden]), .rl-b2b-form__errors:not([hidden])')) openB2bDrawer();
  };

  const initCart = (root) => {
    const cartRoot = root.matches?.('[data-rl-cart]') ? root : root.querySelector?.('[data-rl-cart]');
    if (!cartRoot || cartRoot.dataset.rlCartInitialized === 'true') return;
    cartRoot.dataset.rlCartInitialized = 'true';

    const form = cartRoot.querySelector('#cart');
    const updateButton = form?.querySelector('[data-rl-cart-update]');
    let updateTimer = 0;

    const queueUpdate = () => {
      if (!form || !updateButton) return;
      window.clearTimeout(updateTimer);
      updateTimer = window.setTimeout(() => form.requestSubmit(updateButton), 500);
    };

    cartRoot.querySelectorAll('[data-rl-cart-item]').forEach((item) => {
      const input = item.querySelector('[data-rl-cart-input]');
      const setQuantity = (value) => {
        if (!input) return;
        input.value = Math.max(1, Number(value || 1));
        queueUpdate();
      };

      item.querySelector('[data-rl-cart-minus]')?.addEventListener('click', () => setQuantity(Number(input?.value || 1) - 1));
      item.querySelector('[data-rl-cart-plus]')?.addEventListener('click', () => setQuantity(Number(input?.value || 1) + 1));
      input?.addEventListener('change', () => setQuantity(input.value));
    });

    form?.addEventListener('submit', (event) => {
      if (event.submitter?.name === 'checkout') window.clearTimeout(updateTimer);
    });
  };

  const initRecommendations = (root) => {
    const recommendationBlocks = root.matches?.('[data-rl-recommendations]')
      ? [root]
      : [...(root.querySelectorAll?.('[data-rl-recommendations]') || [])];

    recommendationBlocks.forEach((block) => {
      if (block.dataset.rlInitialized === 'true' || !block.dataset.url) return;
      block.dataset.rlInitialized = 'true';

      fetch(block.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const documentFragment = new DOMParser().parseFromString(text, 'text/html');
          const source = documentFragment.querySelector('[data-rl-recommendations]');
          if (!source?.innerHTML.trim()) return;
          block.innerHTML = source.innerHTML;
          block.hidden = false;
        })
        .catch(() => {});
    });
  };

  const initPredictiveSearch = (root) => {
    const headers = root.matches?.('[data-rl-header]')
      ? [root]
      : [...(root.querySelectorAll?.('[data-rl-header]') || [])];

    headers.forEach((header) => {
      if (header.dataset.rlSearchInitialized === 'true') return;
      header.dataset.rlSearchInitialized = 'true';

      const toggle = header.querySelector('[data-rl-search-toggle]');
      const panel = header.querySelector('[data-rl-predictive-search]');
      const closeButton = panel?.querySelector('[data-rl-search-close]');
      const menuSearch = header.querySelector('[data-rl-menu-search]');
      const form = panel?.querySelector('[data-rl-predictive-form]');
      const input = panel?.querySelector('[data-rl-predictive-input]');
      const results = panel?.querySelector('[data-rl-predictive-results]');
      const status = panel?.querySelector('[data-rl-predictive-status]');
      const viewAll = panel?.querySelector('[data-rl-predictive-all]');
      if (!toggle || !panel || !form || !input || !results || !status || !viewAll) return;

      let searchTimer = 0;
      let requestController = null;
      let requestNumber = 0;

      const setExpanded = (expanded) => {
        toggle.setAttribute('aria-expanded', String(expanded));
        input.setAttribute('aria-expanded', String(expanded && results.children.length > 0));
      };

      const openSearch = () => {
        header.querySelector('.rl-mobile-menu')?.removeAttribute('open');
        document.dispatchEvent(new CustomEvent('rl:mega-close'));
        panel.hidden = false;
        setExpanded(true);
        window.requestAnimationFrame(() => input.focus());
      };

      const closeSearch = () => {
        panel.hidden = true;
        setExpanded(false);
        window.clearTimeout(searchTimer);
        requestController?.abort();
      };

      const showMessage = (message) => {
        results.replaceChildren();
        status.textContent = message;
        viewAll.hidden = true;
        input.setAttribute('aria-expanded', 'false');
      };

      const fetchResults = async (query) => {
        const currentRequest = ++requestNumber;
        requestController?.abort();
        requestController = new AbortController();
        status.textContent = panel.dataset.searching;

        const parameters = new URLSearchParams({ q: query });
        parameters.set('resources[type]', 'product');
        parameters.set('resources[limit]', '6');
        parameters.set('resources[options][unavailable_products]', 'last');
        parameters.set('section_id', 'predictive-search');

        try {
          const response = await fetch(`${panel.dataset.endpoint}?${parameters}`, {
            headers: { Accept: 'text/html' },
            signal: requestController.signal
          });
          if (!response.ok) throw new Error('Predictive search request failed');
          const markup = await response.text();
          if (currentRequest !== requestNumber) return;

          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          const serverResults = documentFragment.querySelector('[data-rl-predictive-items]');
          if (!serverResults) throw new Error('Predictive search markup missing');
          const productResults = [...serverResults.children];
          results.replaceChildren(...productResults);
          status.textContent = productResults.length
            ? `${panel.dataset.resultsLabel} “${query}”`
            : panel.dataset.noResults;
          input.setAttribute('aria-expanded', String(productResults.length > 0));
          viewAll.href = `${form.action}?q=${encodeURIComponent(query)}&type=product`;
          viewAll.hidden = productResults.length === 0;
        } catch (error) {
          if (error.name === 'AbortError') return;
          showMessage(panel.dataset.searchError);
        }
      };

      toggle.addEventListener('click', () => {
        if (panel.hidden) openSearch();
        else closeSearch();
      });
      menuSearch?.addEventListener('click', openSearch);
      closeButton?.addEventListener('click', () => {
        closeSearch();
        toggle.focus();
      });
      input.addEventListener('input', () => {
        const query = input.value.trim();
        window.clearTimeout(searchTimer);
        if (query.length < 2) {
          requestController?.abort();
          showMessage(panel.dataset.minChars);
          return;
        }
        searchTimer = window.setTimeout(() => fetchResults(query), 180);
      });
      form.addEventListener('submit', (event) => {
        if (input.value.trim()) return;
        event.preventDefault();
        input.focus();
      });
      results.addEventListener('click', (event) => {
        if (event.target.closest('a')) closeSearch();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !panel.hidden) {
          closeSearch();
          toggle.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!panel.hidden && !header.contains(event.target)) closeSearch();
      });
    });
  };

  const initCarousels = (root) => {
    const carousels = root.matches?.('[data-rl-carousel]')
      ? [root]
      : [...(root.querySelectorAll?.('[data-rl-carousel]') || [])];

    carousels.forEach((carousel) => {
      if (carousel.dataset.rlInitialized === 'true') return;
      carousel.dataset.rlInitialized = 'true';

      const track = carousel.querySelector('[data-rl-carousel-track]');
      const previous = carousel.querySelector('[data-rl-carousel-prev]');
      const next = carousel.querySelector('[data-rl-carousel-next]');
      if (!track || !previous || !next) return;

      const getStep = () => {
        const firstItem = track.firstElementChild;
        if (!firstItem) return track.clientWidth * 0.8;
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || 0);
        return Math.max(firstItem.getBoundingClientRect().width + gap, track.clientWidth * 0.72);
      };

      const updateControls = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = track.scrollLeft <= 3;
        next.disabled = track.scrollLeft >= maxScroll - 3;
      };

      previous.addEventListener('click', () => track.scrollBy({ left: -getStep(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: getStep(), behavior: 'smooth' }));
      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      updateControls();
    });
  };

  const initQuickAdd = (root) => {
    const forms = root.matches?.('.rl-product-card__form')
      ? [root]
      : [...(root.querySelectorAll?.('.rl-product-card__form') || [])];

    forms.forEach((form) => {
      if (form.dataset.rlInitialized === 'true') return;
      form.dataset.rlInitialized = 'true';

      const button = form.querySelector('button[type="submit"]');
      const label = button?.querySelector('span');
      if (!button || !label) return;

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (button.disabled) return;

        const defaultLabel = button.dataset.defaultLabel || label.textContent;
        button.disabled = true;
        label.textContent = button.dataset.pendingLabel || defaultLabel;

        try {
          await addFormToCart(form);
          button.classList.add('is-added');
          label.textContent = button.dataset.successLabel || defaultLabel;
        } catch (_) {
          document.dispatchEvent(new CustomEvent('rl:cart-error'));
          label.textContent = defaultLabel;
        } finally {
          window.setTimeout(() => {
            button.disabled = false;
            button.classList.remove('is-added');
            label.textContent = defaultLabel;
          }, 1400);
        }
      });
    });
  };

  const initCartDrawer = (root) => {
    const drawers = root.matches?.('[data-rl-cart-drawer]')
      ? [root]
      : [...(root.querySelectorAll?.('[data-rl-cart-drawer]') || [])];

    drawers.forEach((drawer) => {
      if (drawer.dataset.rlInitialized === 'true') return;
      drawer.dataset.rlInitialized = 'true';

      const panel = drawer.querySelector('.rl-cart-drawer__panel');
      const itemsContainer = drawer.querySelector('[data-rl-cart-items]');
      const emptyState = drawer.querySelector('[data-rl-cart-empty]');
      const footer = drawer.querySelector('[data-rl-cart-footer]');
      const subtotal = drawer.querySelector('[data-rl-cart-subtotal]');
      const shipping = drawer.querySelector('[data-rl-cart-shipping]');
      const shippingBar = drawer.querySelector('[data-rl-cart-shipping-bar]');
      const shippingText = drawer.querySelector('[data-rl-cart-shipping-text]');
      const businessCallout = drawer.querySelector('[data-rl-cart-b2b]');
      const status = drawer.querySelector('[data-rl-cart-status]');
      const openers = [...document.querySelectorAll('[data-rl-cart-open]')];
      const closers = [...drawer.querySelectorAll('[data-rl-cart-close]')];
      let lastTrigger = null;

      const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
      })[character]);
      const formatMoney = (cents) => new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);

      const renderCart = (cart) => {
        const hasItems = cart.items.length > 0;
        emptyState.hidden = hasItems;
        footer.hidden = !hasItems;
        shipping.hidden = !hasItems;
        subtotal.textContent = formatMoney(cart.total_price);
        status.textContent = '';
        if (businessCallout) {
          const businessThreshold = Number(drawer.dataset.b2bThreshold || 10);
          businessCallout.hidden = !hasItems || cart.item_count < businessThreshold;
        }

        if (hasItems) {
          const threshold = Number(drawer.dataset.freeShippingThreshold || 0);
          const remaining = Math.max(0, threshold - cart.total_price);
          const progress = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100;
          shippingBar.style.width = `${progress}%`;
          shipping.classList.toggle('is-unlocked', remaining === 0);
          shippingText.textContent = remaining === 0
            ? drawer.dataset.shippingUnlocked
            : drawer.dataset.shippingRemaining.replace('[[amount]]', formatMoney(remaining));
        }

        itemsContainer.innerHTML = cart.items.map((item) => {
          const variant = item.variant_title && item.variant_title !== 'Default Title'
            ? `<small>${escapeHTML(item.variant_title)}</small>`
            : '';
          const image = item.image
            ? `<img src="${escapeHTML(item.image)}" alt="" loading="lazy">`
            : '<span>RL</span>';
          return `
            <article class="rl-cart-drawer__item" data-rl-cart-key="${escapeHTML(item.key)}" data-quantity="${item.quantity}">
              <a class="rl-cart-drawer__item-media" href="${escapeHTML(item.url)}">${image}</a>
              <div class="rl-cart-drawer__item-copy">
                <a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
                ${variant}
                <strong>${formatMoney(item.final_line_price)}</strong>
                <div class="rl-cart-drawer__item-actions">
                  <div>
                    <button type="button" data-rl-cart-action="decrease" aria-label="${escapeHTML(drawer.dataset.decreaseLabel)}">−</button>
                    <span>${item.quantity}</span>
                    <button type="button" data-rl-cart-action="increase" aria-label="${escapeHTML(drawer.dataset.increaseLabel)}">+</button>
                  </div>
                  <button type="button" data-rl-cart-action="remove">${escapeHTML(drawer.dataset.removeLabel)}</button>
                </div>
              </div>
            </article>`;
        }).join('');
      };

      const openDrawer = async (trigger, cart) => {
        lastTrigger = trigger || lastTrigger || document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('rl-cart-drawer-open');
        window.setTimeout(() => drawer.querySelector('[data-rl-cart-close]:not(.rl-cart-drawer__backdrop)')?.focus(), 50);
        if (cart) {
          renderCart(cart);
          return;
        }
        try {
          const currentCart = await getCart();
          publishCart(currentCart, false);
        } catch (_) {
          status.textContent = drawer.dataset.error;
        }
      };

      const closeDrawer = () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('rl-cart-drawer-open');
        lastTrigger?.focus?.();
      };

      const changeCart = async (key, quantity) => {
        drawer.classList.add('is-updating');
        status.textContent = '';
        try {
          const response = await fetch(`${getShopRoot()}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: key, quantity })
          });
          if (!response.ok) throw new Error('Unable to update cart');
          const cart = await response.json();
          publishCart(cart, false);
        } catch (_) {
          status.textContent = drawer.dataset.error;
        } finally {
          drawer.classList.remove('is-updating');
        }
      };

      openers.forEach((opener) => opener.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer(opener);
      }));
      closers.forEach((closer) => closer.addEventListener('click', closeDrawer));
      drawer.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (link && !link.closest('[data-rl-cart-footer]')) closeDrawer();
        const action = event.target.closest('[data-rl-cart-action]');
        if (!action || drawer.classList.contains('is-updating')) return;
        const item = action.closest('[data-rl-cart-key]');
        if (!item) return;
        const quantity = Number(item.dataset.quantity || 0);
        const nextQuantity = action.dataset.rlCartAction === 'remove'
          ? 0
          : Math.max(0, quantity + (action.dataset.rlCartAction === 'increase' ? 1 : -1));
        changeCart(item.dataset.rlCartKey, nextQuantity);
      });
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeDrawer();
      });
      document.addEventListener('rl:cart-updated', (event) => {
        renderCart(event.detail.cart);
        if (event.detail.open) openDrawer(null, event.detail.cart);
      });
      document.addEventListener('rl:cart-error', () => {
        openDrawer().finally(() => { status.textContent = drawer.dataset.error; });
      });
    });
  };

  const initStickyHeader = () => {
    const headerWrap = document.querySelector('.rl-site-header-wrap');
    if (!headerWrap || headerWrap.dataset.rlStickyInitialized === 'true') return;
    headerWrap.dataset.rlStickyInitialized = 'true';

    let compact = false;
    let updateRequested = false;

    const updateHeader = () => {
      const scrollTop = Math.max(window.scrollY, document.documentElement.scrollTop || 0);
      if (!compact && scrollTop > 96) compact = true;
      if (compact && scrollTop < 18) compact = false;
      headerWrap.classList.toggle('is-scrolled', compact);
      updateRequested = false;
    };

    const requestUpdate = () => {
      if (updateRequested) return;
      updateRequested = true;
      window.requestAnimationFrame(updateHeader);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    updateHeader();
  };

  const initMegaNavigation = (root) => {
    const navigations = root.matches?.('[data-rl-mega-nav]')
      ? [root]
      : [...(root.querySelectorAll?.('[data-rl-mega-nav]') || [])];

    navigations.forEach((navigation) => {
      if (navigation.dataset.rlInitialized === 'true') return;
      navigation.dataset.rlInitialized = 'true';

      const triggers = [...navigation.querySelectorAll('[data-rl-mega-trigger]')];
      const panels = [...navigation.querySelectorAll('[data-rl-mega-panel]')];
      const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      let hoverTimer = 0;
      let activeTrigger = null;

      const closeAll = (restoreFocus = false) => {
        window.clearTimeout(hoverTimer);
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        panels.forEach((panel) => { panel.hidden = true; });
        navigation.classList.remove('is-open');
        if (restoreFocus) activeTrigger?.focus();
        activeTrigger = null;
      };

      const openPanel = (trigger) => {
        const panel = navigation.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
        if (!panel) return;
        closeAll();
        trigger.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        navigation.classList.add('is-open');
        activeTrigger = trigger;
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) closeAll();
          else openPanel(trigger);
        });
        trigger.addEventListener('pointerenter', () => {
          if (!hoverQuery.matches) return;
          window.clearTimeout(hoverTimer);
          hoverTimer = window.setTimeout(() => openPanel(trigger), 140);
        });
      });

      navigation.addEventListener('pointerenter', () => window.clearTimeout(hoverTimer));
      navigation.addEventListener('pointerleave', () => {
        if (!hoverQuery.matches) return;
        hoverTimer = window.setTimeout(() => closeAll(), 240);
      });
      navigation.addEventListener('click', (event) => {
        if (event.target.closest('[data-rl-mega-panel] a')) closeAll();
      });
      document.addEventListener('click', (event) => {
        if (!navigation.contains(event.target)) closeAll();
      });
      document.addEventListener('focusin', (event) => {
        if (!navigation.contains(event.target)) closeAll();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && activeTrigger) closeAll(true);
      });
      document.addEventListener('rl:mega-close', () => closeAll());
    });

    root.querySelectorAll?.('.rl-mobile-menu__categories').forEach((categoryList) => {
      if (categoryList.dataset.rlInitialized === 'true') return;
      categoryList.dataset.rlInitialized = 'true';
      categoryList.querySelectorAll('.rl-mobile-category').forEach((category) => {
        category.addEventListener('toggle', () => {
          if (!category.open) return;
          categoryList.querySelectorAll('.rl-mobile-category[open]').forEach((other) => {
            if (other !== category) other.open = false;
          });
        });
      });
    });
  };

  const initGlobalControls = (root) => {
    root.querySelectorAll?.('[data-rl-home-search]').forEach((button) => {
      if (button.dataset.rlInitialized === 'true') return;
      button.dataset.rlInitialized = 'true';
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const searchToggle = document.querySelector('[data-rl-header] [data-rl-search-toggle]');
        if (searchToggle) searchToggle.click();
        else window.location.href = button.dataset.searchUrl || '/search';
      });
    });

    root.querySelectorAll?.('[data-rl-sort]').forEach((select) => {
      if (select.dataset.rlInitialized === 'true') return;
      select.dataset.rlInitialized = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit());
    });

    root.querySelectorAll?.('.rl-mobile-menu').forEach((menu) => {
      if (menu.dataset.rlInitialized === 'true') return;
      menu.dataset.rlInitialized = 'true';
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.open = false;
          menu.querySelector('summary')?.focus();
        }
      });
      menu.addEventListener('toggle', () => {
        document.documentElement.classList.toggle('rl-mobile-menu-is-open', menu.open);
        const summary = menu.querySelector('summary');
        if (summary) summary.setAttribute('aria-label', menu.open ? summary.dataset.closeLabel : summary.dataset.openLabel);
      });
      menu.querySelector('[data-rl-menu-close]')?.addEventListener('click', () => { menu.open = false; });
      menu.querySelectorAll('nav a').forEach((link) => link.addEventListener('click', () => { menu.open = false; }));
      document.addEventListener('click', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
    });

    root.querySelectorAll?.('.rl-header-more').forEach((menu) => {
      if (menu.dataset.rlInitialized === 'true') return;
      menu.dataset.rlInitialized = 'true';
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.open = false;
          menu.querySelector('summary')?.focus();
        }
      });
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => { menu.open = false; }));
      document.addEventListener('click', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
    });

    root.querySelectorAll?.('[data-rl-facets]').forEach((facets) => {
      if (facets.dataset.rlResponsiveInitialized === 'true') return;
      facets.dataset.rlResponsiveInitialized = 'true';
      if (window.innerWidth <= 820) facets.open = false;
    });
  };

  const initAll = (scope = document) => {
    initStickyHeader();
    initCartDrawer(scope);
    scope.querySelectorAll('[data-rl-product-section]').forEach(initProduct);
    initLeadForms(scope);
    initCart(scope);
    initRecommendations(scope);
    initPredictiveSearch(scope);
    initMegaNavigation(scope);
    initCarousels(scope);
    initQuickAdd(scope);
    initGlobalControls(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
